import * as Sentry from '@sentry/browser';
import { type DocumentNode, Kind, type OperationDefinitionNode } from 'graphql';
import { GraphQLClient, type RequestDocument } from 'graphql-request';
import { jwtDecode } from 'jwt-decode';
import type { MarketCode, Token } from '../@types/Token';
import { getToken } from '../providers/auth';

export const getTokenHeader = async (token: string | Promise<string | undefined>) => {
  const headers = new Headers();
  const _token = (typeof token === 'string' ? token : await token) || '';

  headers.append('Authorization', `Bearer ${_token}`);

  return headers;
};

export const getDefaultHeaders = async (token?: string | Promise<string | undefined>) => {
  const headers = new Headers();

  if (token) {
    const _token = (typeof token === 'string' ? token : await token) || '';
    headers.append('Authorization', `Bearer ${_token}`);
  }
  headers.append('Content-Type', 'application/json');
  return headers;
};

export const createBody = (body: any) => JSON.stringify(body);

export interface RequestError<T = undefined> {
  message: string;
  statusCode: number;
  body?: T;
}

export interface RequestOptions {
  signal?: AbortSignal;
}

interface LogSentryOptions {
  method: string;
  message: string;
  body?: any;
  token?: string | Promise<string | undefined>;
}
const guidRegex =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/g;

const normalizeUrl = (url: string) => url.replace(guidRegex, ':GUID');

interface FetchErrorOptions {
  url: string;
  status: number;
  method: string;
  responseBody?: any;
  sub?: string;
  'belongs-to-branch'?: string[];
  'market-code'?: MarketCode;
  role?: string;
}

class FetchError extends Error {
  errorOptions: FetchErrorOptions;

  constructor(message: string, errorOptions: FetchErrorOptions) {
    super(message);
    this.name = 'FetchError';
    this.errorOptions = errorOptions;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }
  }

  addRequestBody(body?: any) {
    if (!body) return this;
    this.errorOptions.responseBody = body;
    return this;
  }

  addTokenInformation(token?: string) {
    if (!token) return this;

    const decodedToken = jwtDecode<Token>(token);
    this.errorOptions.sub = decodedToken.sub;
    this.errorOptions['belongs-to-branch'] = decodedToken['belongs-to-branch'];
    this.errorOptions['market-code'] = decodedToken['market-code'];
    this.errorOptions.role = decodedToken.role;
    return this;
  }

  captureException() {
    Sentry.captureException(this, {
      extra: this.errorOptions as { [key: string]: any },
    });
    return this;
  }
}

const omitErrorsFromVehicle = (statusCode: number, _url: string) => {
  const url = new URL(_url);
  if (
    statusCode === 422 &&
    url.pathname.startsWith('/api/vehicle') &&
    url.pathname.endsWith('/detailed')
  ) {
    return true;
  }
  return false;
};

const logSentry = async (response: Response, options: LogSentryOptions) => {
  if (
    [401, 403, 404].includes(response.status) ||
    omitErrorsFromVehicle(response.status, response.url)
  ) {
    return;
  }

  const resolvedToken = options.token
    ? (typeof options.token === 'string' ? options.token : await options.token) || ''
    : undefined;

  new FetchError(options.message, {
    url: response.url,
    status: response.status,
    method: options.method,
  })
    .addRequestBody(options.body)
    .addTokenInformation(resolvedToken)
    .captureException();
};

export const createRequestError = async <T = undefined>(
  response: Response,
  method: string,
): Promise<RequestError<T>> => {
  const message = `[${method} ${response.status}] - ${normalizeUrl(response.url)}`;
  try {
    const body = await response.json();
    return {
      statusCode: response.status,
      message,
      body,
    };
  } catch (_e) {
    return {
      statusCode: response.status,
      message,
    };
  }
};

const resolveMethod = (method: string) => {
  switch (method) {
    case 'TEXT':
    case 'BLOB':
      return 'GET';
    case 'POSTBLOB':
      return 'POST';
    default:
      return method;
  }
};

const fetchJson = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  method: 'GET' | 'BLOB' | 'POSTBLOB' | 'PATCH' | 'POST' | 'PUT' | 'DELETE' | 'TEXT',
  body?: any,
  form?: boolean,
  options?: RequestOptions,
) => {
  const headers =
    form || method === 'TEXT' ? await getTokenHeader(token) : await getDefaultHeaders(token);
  if (method === 'TEXT') {
    headers.set('Content-Type', 'text/html; charset=utf-8');
  }

  const _body = body ? (form ? (body as FormData) : createBody(body)) : undefined;
  const resolvedMethod = resolveMethod(method);

  const response = await fetch(url, {
    method: resolvedMethod,
    headers,
    body: _body,
    signal: options?.signal,
  });

  if (!response.ok) {
    const requestError = await createRequestError(response, resolvedMethod);
    await logSentry(response, {
      message: requestError.message,
      method: resolvedMethod,
      body: requestError.body,
      token,
    });

    throw requestError;
  }

  if (method === 'BLOB' || method === 'POSTBLOB') {
    return await response.blob();
  } else if (method === 'TEXT') {
    return await response.text();
  }
  // remove 201 for useSingleMedia
  // add 201 for process-flow
  if (![...[202, 204], ...(url.includes('gateway') ? [201] : [])].includes(response.status))
    return (await response.json()) as T;
  return _body as T;
};

export const getJSONUnauthorized = async <T,>(url: string) => fetchJson<T>(url, '', 'GET') as T;

export const postJSONUnauthorized = async <T,>(url: string, body?: any) =>
  fetchJson<T>(url, '', 'POST', body) as T;

export const getJSON = async <T,>(url: string, token: string | Promise<string | undefined>) =>
  fetchJson<T>(url, token, 'GET') as T;

export const getBlob = async (url: string, token: string | Promise<string | undefined>) =>
  fetchJson<Blob>(url, token, 'BLOB') as Promise<Blob>;
export const getText = async (url: string, token: string | Promise<string | undefined>) =>
  fetchJson<string>(url, token, 'TEXT') as Promise<string>;

export const postJSON = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
  options?: RequestOptions,
) => fetchJson<T>(url, token, 'POST', body, false, options) as T;

export const postBlob = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
) => fetchJson<T>(url, token, 'POSTBLOB', body) as T;

export const postForm = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body: FormData,
  options?: RequestOptions,
) => fetchJson<T>(url, token, 'POST', body, true, options) as T;

export const putJSON = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
) => fetchJson<T>(url, token, 'PUT', body) as T;

export const putJSONWithHeaders = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
  additionalHeaders?: Record<string, string>,
) => {
  const headers = await getDefaultHeaders(token);

  if (additionalHeaders) {
    Object.entries(additionalHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: body ? createBody(body) : undefined,
  });

  if (!response.ok) {
    const requestError = await createRequestError(response, 'PUT');
    await logSentry(response, {
      message: requestError.message,
      method: 'PUT',
      body: requestError.body,
      token,
    });

    throw requestError;
  }

  if (![202, 204].includes(response.status)) return (await response.json()) as T;
  return body as T;
};

export const patchJSON = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
) => fetchJson<T>(url, token, 'PATCH', body) as T;

export const patchJSONWithHeaders = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
  body?: any,
  additionalHeaders?: Record<string, string>,
) => {
  const headers = await getDefaultHeaders(token);

  if (additionalHeaders) {
    Object.entries(additionalHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  const response = await fetch(url, {
    method: 'PATCH',
    headers,
    body: body ? createBody(body) : undefined,
  });

  if (!response.ok) {
    const requestError = await createRequestError(response, 'PATCH');
    await logSentry(response, {
      message: requestError.message,
      method: 'PATCH',
      body: requestError.body,
      token,
    });

    throw requestError;
  }

  if (![202, 204].includes(response.status)) return (await response.json()) as T;
  return body as T;
};

export const deleteJSON = async <T = undefined>(
  url: string,
  token: string | Promise<string | undefined>,
) => fetchJson<T>(url, token, 'DELETE') as T;

const getQueryName = (document: RequestDocument): string | undefined => {
  if (typeof document === 'string') {
    // Find operation name anywhere in the string (not just at the start)
    // This handles cases where fragments are defined before the operation
    const match = document.match(/(query|mutation|subscription)\s+(\w+)/);
    return match?.[2];
  }

  const docNode = document as DocumentNode;
  const definition = docNode.definitions.find(
    (def): def is OperationDefinitionNode => def.kind === Kind.OPERATION_DEFINITION,
  );

  // Try to get name from AST first
  if (definition?.name?.value) {
    return definition.name.value;
  }

  // Fallback: try to extract from source if available
  const source = docNode.loc?.source?.body;
  if (source) {
    const match = source.match(/(query|mutation|subscription)\s+(\w+)/);
    return match?.[2];
  }

  return undefined;
};

export const getGql = async <T = undefined>(
  url: string,
  document: RequestDocument,
  variables: any,
  signal?: AbortSignal,
  xOwnerId?: string,
) => {
  const token = await getToken();
  const headers = new Headers();
  headers.append('authorization', `Bearer ${token}`);
  if (xOwnerId) {
    headers.append('X-Owner-Id', xOwnerId);
  }

  const queryName = getQueryName(document);
  const urlWithQueryName = queryName
    ? `${url}${url.includes('?') ? '&' : '?'}opName=${queryName}`
    : url;

  const client = new GraphQLClient(urlWithQueryName, { headers });
  return client.request<T>({ document, variables, signal, requestHeaders: headers }) as Promise<T>;
};
